import { StandardEvents } from '@shopify/events';
import { prefersReducedMotion } from '@theme/utilities';
import '@theme/fly-to-cart';

let toastTimer;
const upsellButtonSelector = '#cart-drawer.ryoxzel-cart .ryoxzel-cart__upsell-add[type="submit"]';
const pendingForms = new WeakMap();

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('button[type="submit"][name="add"]') : null;
  const form = button?.closest('product-form-component');
  const drawer = document.getElementById('cart-drawer');

  if (!button || !form || !(drawer instanceof HTMLElement)) return;

  // Keep one request record per product form. This deliberately does not
  // interrupt Horizon's click handler or replace its button markup.
  const existing = pendingForms.get(form);
  if (existing?.state === 'pending') event.preventDefault();

  const addToCart = button.closest('add-to-cart-component');
  pendingForms.set(form, {
    button,
    image: addToCart?.dataset.productVariantMedia || '',
    recordedAt: Date.now(),
    isUpsell: button.matches(upsellButtonSelector),
    state: 'ready',
  });
}, true);

document.addEventListener('submit', (event) => {
  const nativeForm = event.target;
  if (!(nativeForm instanceof HTMLFormElement)) return;

  const form = nativeForm.closest('product-form-component');
  const button = event.submitter instanceof HTMLButtonElement ? event.submitter : null;
  if (!form || !button) return;

  const add = pendingForms.get(form);
  if (!add || add.button !== button || add.state === 'pending') return;

  add.state = 'pending';
  if (add.isUpsell) setUpsellButtonState(button, 'pending');

  // The pending record is checked on every later click. Do not set the native
  // disabled property here: Horizon reads that property while starting its
  // own asynchronous form submission.
}, true);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add' || !(event.target instanceof HTMLElement)) return;

  const form = event.target.closest('product-form-component');
  const add = form ? pendingForms.get(form) : null;
  if (!add || add.state !== 'pending') return;

  event.promise
    ?.then(({ detail }) => {
      pendingForms.delete(form);

      if (detail?.didError || Date.now() - add.recordedAt > 15000) {
        restoreUpsellButton(add.button);
        return;
      }

      if (add.isUpsell) setUpsellButtonState(add.button, 'added');

      const drawer = document.getElementById('cart-drawer');
      if (!(drawer instanceof HTMLElement)) return;

      if (drawer.dataset.ryoxzelMotionEnabled === 'true' && !prefersReducedMotion()) {
        playFlyer(add.button, add.image);
      }

      if (drawer.dataset.ryoxzelToastEnabled === 'true') showToast();
    })
    .catch(() => {
      pendingForms.delete(form);
      restoreUpsellButton(add.button);
      // Horizon's product form owns presentation of real cart errors.
    });
});

document.addEventListener('input', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLTextAreaElement) || !input.matches('#cart-drawer .cart-note__instructions')) return;

  const count = document.querySelector('#cart-drawer #ryoxzel-cart-note-count');
  if (count) count.textContent = `${input.value.length} / 180`;
});

document.addEventListener('click', (event) => {
  const done = event.target instanceof Element ? event.target.closest('[data-ryoxzel-cart-note-done]') : null;
  if (!done) return;

  const details = done.closest('details');
  details?.removeAttribute('open');
});

// `close` does not bubble, so the capture phase is what reaches the document.
// A toast must never outlive the drawer it belongs to.
document.addEventListener(
  'close',
  (event) => {
    if (event.target instanceof Element && event.target.closest('#cart-drawer')) clearToast();
  },
  true
);

function playFlyer(source, image) {
  const destination = document.querySelector('.header-actions__cart-icon');
  if (!destination || !image) return;

  const flyer = document.createElement('fly-to-cart');
  flyer.classList.add('fly-to-cart--main');
  flyer.style.setProperty('background-image', `url(${image})`);
  flyer.style.setProperty('--start-opacity', '0');
  flyer.source = source;
  flyer.destination = destination;
  document.body.appendChild(flyer);
}

function showToast() {
  const panel = toastPanel();

  // No open drawer means no surface to confirm on. Adding from a product page,
  // a shelf card or the PDP Pair With rows keeps the button's own added state
  // (and, when enabled, the fly-to-cart motion) as the feedback.
  if (!panel) {
    clearToast();
    return;
  }

  let toast = panel.querySelector('.ryoxzel-cart__toast');
  if (!(toast instanceof HTMLElement)) {
    toast = document.createElement('div');
    toast.className = 'ryoxzel-cart__toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    panel.appendChild(toast);
  }

  toast.textContent = 'Added to cart';
  toast.classList.add('is-visible');
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    const current = panel.querySelector('.ryoxzel-cart__toast');
    current?.classList.remove('is-visible');
    current?.remove();
  }, 1800);
}

/**
 * The toast is an absolutely positioned layer that belongs to the drawer
 * surface, so it may only be rendered onto an open panel.
 *
 * `<theme-drawer id="cart-drawer">` is `display: contents` — it generates no
 * box of its own, so an absolutely positioned child appended to that host has
 * `<body>` as its containing block and paints at a fixed page position instead
 * of inside the drawer. That read as a stray "Added to cart" pill floating in
 * the middle of the viewport on mobile (the shop does not auto-open the drawer
 * on add: `auto_open_cart_drawer` is off).
 *
 * @returns {HTMLElement | null} The open drawer panel, or null when it is closed.
 */
function toastPanel() {
  const drawer = document.getElementById('cart-drawer');
  if (!(drawer instanceof HTMLElement) || !drawer.hasAttribute('open')) return null;

  const panel =
    drawer.querySelector('dialog.theme-drawer__dialog') ?? drawer.querySelector('dialog');

  return panel instanceof HTMLElement ? panel : null;
}

function clearToast() {
  for (const toast of document.querySelectorAll('#cart-drawer .ryoxzel-cart__toast')) {
    toast.remove();
  }
}

function setUpsellButtonState(button, state) {
  if (!button.matches(upsellButtonSelector)) return;

  if (state === 'pending') {
    button.dataset.ryoxzelCartPending = 'true';
    button.setAttribute('aria-busy', 'true');
    return;
  }

  clearUpsellButtonState(button);
  button.dataset.ryoxzelCartAdded = 'true';
  window.setTimeout(() => restoreUpsellButton(button), 250);
}

function restoreUpsellButton(button) {
  if (!button.matches(upsellButtonSelector)) return;

  clearUpsellButtonState(button);
}

function clearUpsellButtonState(button) {
  button.removeAttribute('data-ryoxzel-cart-pending');
  button.removeAttribute('data-ryoxzel-cart-added');
  button.removeAttribute('aria-busy');
}
